/**
 * Sponsored Impressions — viewport-based impression reporting
 *
 * An impression means the sponsored card actually entered the viewport, not
 * that a feed response contained it. Cards report through
 * `useSponsoredImpression`; reports are deduped per session, queued, and
 * flushed in debounced batches to POST /api/sponsored/impressions.
 */

import { useEffect, useRef } from 'react';
import { API_BASE } from '../config/api';
import AuthService from './authService';

const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 50;

const seenThisSession = new Set(); // campaign IDs already reported
let queue = [];
let flushTimer = null;

async function flush() {
  flushTimer = null;
  if (queue.length === 0) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  try {
    await fetch(`${API_BASE}/api/sponsored/impressions`, {
      method: 'POST',
      headers: AuthService.getJsonHeaders(),
      body: JSON.stringify({ impressions: batch })
    });
  } catch (e) {
    // Impression loss is acceptable — never disrupt the feed for tracking
  }
  if (queue.length > 0) scheduleFlush();
}

function scheduleFlush() {
  if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

export function reportSponsoredImpression({ sponsoredContentId, token, organicRank, boostedRank, queryContext }) {
  if (!sponsoredContentId || seenThisSession.has(sponsoredContentId)) return;
  seenThisSession.add(sponsoredContentId);
  queue.push({
    sponsored_content_id: sponsoredContentId,
    // Server-stamped delivery token — the server drops reports without it
    impression_token: token || null,
    organic_rank: organicRank ?? null,
    boosted_rank: boostedRank ?? null,
    query_context: queryContext || null
  });
  scheduleFlush();
}

/**
 * Read sponsored annotations from either a normalized card (normalizeCardData
 * fields) or a raw API item (applyBoosts `_`-prefixed fields).
 */
function extractSponsoredFields(data) {
  if (!data) return { sponsoredContentId: null };
  return {
    sponsoredContentId: data.sponsoredContentId || data._sponsoredContentId || null,
    token: data.sponsoredToken || data._sponsoredToken || null,
    organicRank: data.sponsoredOrganicRank ?? data._organicRank ?? null,
    boostedRank: data.sponsoredBoostedRank ?? data._boostedRank ?? null,
    queryContext: data.sponsoredQueryContext || data._queryContext || null
  };
}

/**
 * Attach the returned ref to a card's root element. When the card is at least
 * half visible for the first time, one impression is reported. No-op for
 * non-sponsored cards (null/undefined campaign ID). Accepts normalized card
 * data or raw API items.
 */
export function useSponsoredImpression(data) {
  const ref = useRef(null);
  const fields = extractSponsoredFields(data);
  const campaignId = fields.sponsoredContentId;

  useEffect(() => {
    const el = ref.current;
    if (!el || !campaignId || seenThisSession.has(campaignId)) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          reportSponsoredImpression(extractSponsoredFields(data));
          observer.disconnect();
          break;
        }
      }
    }, { threshold: 0.5 });

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  return ref;
}
